// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { SupportSnackbar } from '@/components/support-snackbar';
import { useToast } from '@/contexts/toast-context';
import { getItem } from '@/items/api/get-item';
import {
  SKU_PRICING_DEFAULT_ERROR_TOAST_MESSAGE,
  SKU_PRICING_DEFAULT_TOAST_ANCHOR_ORIGIN,
  SKU_PRICING_DEFAULT_TOAST_DURATION_MS,
} from '@/items/constants/pricing-default-toast';
import { ProjectLineItem } from '@/project/api/types';
import { BillingPlanDrawer } from '@/project/components/billing-plan-drawer';
import { useProjectLineItem } from '@/project/hooks/use-project-line-item';
import {
  deriveProjectCurrency,
  lineItemCurrencies,
} from '@/project/utilities/project-currency';
import { captureException } from '@sentry/react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useEffect, useRef, useState } from 'react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectKey: null | string;
  lineItemKey: null | string;
  additionalInvalidations?: string[][];
  focusChargeKey?: string;
  onFocusChargeComplete?: () => void;
}

function hasStoredPricingValues(lineItem: ProjectLineItem | null): boolean {
  if (lineItem === null) {
    return false;
  }

  return (
    lineItem.pricingMode !== null ||
    lineItem.unitPrice !== null ||
    lineItem.fixedPrice !== null
  );
}

// Thin wrapper that owns data plumbing so the inner drawer stays
// mounted across loading/loaded/closed transitions (a single Drawer
// instance preserves MUI's slide animation and doesn't remount).
export function BillingPlanDrawerContainer(props: Props) {
  const [showLoadErrorSnackbar, setShowLoadErrorSnackbar] = useState(false);
  const pricingDefaultErrorToastItemKeyRef = useRef<string | null>(null);
  const pricingDefaultSuccessToastItemKeyRef = useRef<string | null>(null);
  const { showToast } = useToast();
  const { project, lineItem, isLoading, isError, error } = useProjectLineItem({
    projectKey: props.projectKey,
    lineItemKey: props.lineItemKey,
  });

  // The line item adopts the currency established by another project field
  // (contract value, discount, or a sibling line item), locking its selector.
  // When this line item is the project's only currency-bearing field it stays
  // editable so it can be re-denominated.
  const otherLineItems = (project?.items ?? []).filter(
    (item) => item.key !== props.lineItemKey
  );
  const projectCurrencyLock = !project
    ? undefined
    : (deriveProjectCurrency([
        project.totalContractValue,
        project.discount,
        ...lineItemCurrencies(otherLineItems),
      ]) ?? undefined);
  const pricingDefaultItemKey = lineItem?.item.key ?? null;
  const pricingDefaultItemName = lineItem?.item.name ?? null;
  const shouldLoadPricingDefault =
    props.isOpen &&
    pricingDefaultItemKey !== null &&
    !hasStoredPricingValues(lineItem);
  const pricingDefaultQuery = useQuery({
    queryKey: ['item', pricingDefaultItemKey],
    queryFn: () => {
      if (pricingDefaultItemKey === null) {
        throw new Error('Item key is required');
      }
      return getItem(pricingDefaultItemKey);
    },
    enabled: shouldLoadPricingDefault,
  });
  const defaultPricing = shouldLoadPricingDefault
    ? (pricingDefaultQuery.data?.item.defaultPricing ?? null)
    : null;
  const hasPricingDefault = defaultPricing !== null;

  // Surface load failures as a snackbar (and Sentry) instead of an
  // inline alert: the drawer body is otherwise just skeletons, and
  // an inline error stranded in a slide-out drawer is easy to miss
  // when the drawer animates away on close. Also covers the
  // resolved-but-not-present case (the project loaded but the
  // line item key isn't on it) — same UX, no Sentry since that's a
  // 404-equivalent rather than a system error.
  const notFound =
    props.projectKey !== null &&
    props.lineItemKey !== null &&
    !isLoading &&
    !isError &&
    lineItem === null;
  useEffect(() => {
    if (!props.isOpen) {
      return;
    }
    if (isError) {
      captureException(error ?? new Error('Failed to load billing plan'));
      setShowLoadErrorSnackbar(true);
      props.onClose();
      return;
    }
    if (notFound) {
      setShowLoadErrorSnackbar(true);
      props.onClose();
    }
  }, [props.isOpen, isError, error, notFound, props]);

  useEffect(() => {
    if (!props.isOpen) {
      pricingDefaultErrorToastItemKeyRef.current = null;
      return;
    }
    if (
      !shouldLoadPricingDefault ||
      !pricingDefaultQuery.isError ||
      pricingDefaultItemKey === null ||
      pricingDefaultErrorToastItemKeyRef.current === pricingDefaultItemKey
    ) {
      return;
    }

    pricingDefaultErrorToastItemKeyRef.current = pricingDefaultItemKey;
    const pricingDefaultError =
      pricingDefaultQuery.error ??
      new Error('Failed to load SKU pricing defaults');
    if (axios.isAxiosError(pricingDefaultError)) {
      const apiError = handleAPIError(pricingDefaultError);
      if (!apiError.sentToSentry) {
        captureException(pricingDefaultError);
      }
    } else {
      captureException(pricingDefaultError);
    }
    showToast({
      severity: 'error',
      element: <span>{SKU_PRICING_DEFAULT_ERROR_TOAST_MESSAGE}</span>,
      duration: SKU_PRICING_DEFAULT_TOAST_DURATION_MS,
      anchorOrigin: SKU_PRICING_DEFAULT_TOAST_ANCHOR_ORIGIN,
    });
  }, [
    props.isOpen,
    shouldLoadPricingDefault,
    pricingDefaultQuery.isError,
    pricingDefaultQuery.error,
    pricingDefaultItemKey,
    showToast,
  ]);

  useEffect(() => {
    if (!props.isOpen) {
      pricingDefaultSuccessToastItemKeyRef.current = null;
      return;
    }
    if (
      !shouldLoadPricingDefault ||
      !pricingDefaultQuery.isSuccess ||
      !hasPricingDefault ||
      pricingDefaultItemKey === null ||
      pricingDefaultItemName === null ||
      pricingDefaultSuccessToastItemKeyRef.current === pricingDefaultItemKey
    ) {
      return;
    }

    pricingDefaultSuccessToastItemKeyRef.current = pricingDefaultItemKey;
    showToast({
      severity: 'success',
      element: (
        <span>{`Pricing defaults loaded for ${pricingDefaultItemName}`}</span>
      ),
      duration: SKU_PRICING_DEFAULT_TOAST_DURATION_MS,
      anchorOrigin: SKU_PRICING_DEFAULT_TOAST_ANCHOR_ORIGIN,
    });
  }, [
    props.isOpen,
    shouldLoadPricingDefault,
    pricingDefaultQuery.isSuccess,
    hasPricingDefault,
    pricingDefaultItemKey,
    pricingDefaultItemName,
    showToast,
  ]);

  // Render-at-root: the drawer must stay mounted even when keys are
  // absent (post-close) so MUI plays its slide-out transition. We
  // pass `isOpen={false}` and skip data-driven state in that case.
  if (props.projectKey === null || props.lineItemKey === null) {
    return (
      <>
        <BillingPlanDrawer
          isOpen={false}
          onClose={props.onClose}
          lineItem={null}
          projectKey={props.projectKey ?? ''}
          additionalInvalidations={props.additionalInvalidations}
        />
        <SupportSnackbar
          isOpen={showLoadErrorSnackbar}
          onClose={() => setShowLoadErrorSnackbar(false)}
          message="Could not load the billing plan. Try reopening the drawer."
        />
      </>
    );
  }

  return (
    <>
      <BillingPlanDrawer
        isOpen={props.isOpen}
        onClose={props.onClose}
        lineItem={lineItem}
        defaultPricing={defaultPricing}
        projectKey={props.projectKey}
        projectCurrencyLock={projectCurrencyLock}
        additionalInvalidations={props.additionalInvalidations}
        focusChargeKey={props.focusChargeKey}
        onFocusChargeComplete={props.onFocusChargeComplete}
        isLoading={
          isLoading ||
          (shouldLoadPricingDefault && pricingDefaultQuery.isLoading)
        }
      />
      <SupportSnackbar
        isOpen={showLoadErrorSnackbar}
        onClose={() => setShowLoadErrorSnackbar(false)}
        message="Could not load the billing plan. Try reopening the drawer."
      />
    </>
  );
}
